import React, { PureComponent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { branch } from "baobab-react/higher-order";
import autoBind from "react-autobind";
import DriveContext from "../../driveContext";
import styles from "./listNode.module.css";
import {
	SKU_PHOTO_MAPPING_TILE_ACTION,
	getSkuPhotoMappingTileClickAction,
	shouldShowDriveSelectionControl
} from "./skuPhotoMappingTileInteraction";

import { generateUUID, getFileTypeIcon } from "../../../../utils/utils";

const DOUBLE_TAP_MS = 450;
const SYNTHETIC_CLICK_SUPPRESS_MS = 500;

function isAcceptedNodeType(type, node, accept) {
	if (type === "folder" || !accept) {
		return true;
	}

	const acceptedTypes = Array.isArray(accept) ? accept : [accept];
	const normalize = (value) => `${value || ""}`.trim().toLowerCase();
	const nodeType = normalize(node && node.metaData ? node.metaData.type : "");
	const contentType = normalize(node && (node.contentType || node.mimeType || (node.metaData && (node.metaData.contentType || node.metaData.mimeType))));
	const explicitExtension = normalize(node && (node.ext || node.extension)).replace(/^\./, "");
	const pathCandidate = normalize(node && (node.name || node.fileName || node.fullPath || (Array.isArray(node.path) ? node.path.join("/") : node.path)));
	const pathExtension = pathCandidate.includes(".") ? pathCandidate.split(".").pop() : "";
	const extension = explicitExtension || pathExtension;
	const extensionType = extension ? `extension/${extension}` : "";

	return acceptedTypes.some((acceptedTypeValue) => {
		const acceptedType = normalize(acceptedTypeValue);
		return (
		acceptedType === nodeType
		|| acceptedType === contentType
		|| acceptedType === extensionType
		|| acceptedType === extension
		|| (acceptedType === "application/json" && extension === "json")
		);
	});
}

class ListNode extends PureComponent {
	constructor(props) {
		super(props);
		this.lastTouchTapAt = 0;
		this.lastTouchTapKey = null;
		this.suppressClickUntil = 0;
		this.touchStart = null;
		autoBind(this);
	}

	getTapKey() {
		const { node } = this.props;
		return node.guid || node.id || node.fullPath || node.path || node.name;
	}

	handleClick(event) {
		if (Date.now() < this.suppressClickUntil) {
			return;
		}

		const mappingAction = getSkuPhotoMappingTileClickAction({
			skuPhotoMappingMode: this.props.skuPhotoMappingMode,
			node: this.props.node,
			selectionAvailable: this.props.selectionAvailable,
			clickDetail: event?.detail
		});
		if (mappingAction === SKU_PHOTO_MAPPING_TILE_ACTION.OPEN_FOLDER) {
			this.handleDblClick();
			return;
		}
		if (mappingAction === SKU_PHOTO_MAPPING_TILE_ACTION.TOGGLE_SELECTION) {
			this.handleToggleSelected(event);
			return;
		}
		if (mappingAction === SKU_PHOTO_MAPPING_TILE_ACTION.IGNORE) {
			return;
		}

		if (event.detail === 2) {
			this.handleDblClick();
		} else {
			this.handleSelect();
		}
	}

	handleTouchStart(event) {
		const touch = event.touches && event.touches[0];
		this.touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
	}

	handleTouchEnd(event) {
		if (this.props.node.type !== "folder") {
			return;
		}

		const touch = event.changedTouches && event.changedTouches[0];
		if (this.touchStart && touch) {
			const deltaX = Math.abs(touch.clientX - this.touchStart.x);
			const deltaY = Math.abs(touch.clientY - this.touchStart.y);
			if (deltaX > 10 || deltaY > 10) {
				this.touchStart = null;
				return;
			}
		}
		this.touchStart = null;

		event.preventDefault();
		event.stopPropagation();
		this.suppressClickUntil = Date.now() + SYNTHETIC_CLICK_SUPPRESS_MS;

		const now = Date.now();
		const tapKey = this.getTapKey();
		const isDoubleTap = this.lastTouchTapKey === tapKey && now - this.lastTouchTapAt <= DOUBLE_TAP_MS;

		this.lastTouchTapAt = isDoubleTap ? 0 : now;
		this.lastTouchTapKey = isDoubleTap ? null : tapKey;

		if (this.props.skuPhotoMappingMode) {
			this.lastTouchTapAt = 0;
			this.lastTouchTapKey = null;
			this.handleDblClick();
		} else if (isDoubleTap) {
			this.handleDblClick();
		} else {
			this.handleSelect();
		}
	}

	handleMappingKeyDown(event) {
		if (!this.props.skuPhotoMappingMode || (event.key !== "Enter" && event.key !== " ")) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		if (this.props.node.type === "folder") {
			this.handleDblClick();
		}
	}

	handleSelect() {
		if (this.props.onSelect) {
			this.props.onSelect(this.props.node);
		}
	}

	handleToggleSelected(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (this.props.selectionAvailable && this.props.onToggleSelected) {
			this.props.onToggleSelected(this.props.node);
		}
	}

	handleDblClick() {
		if (this.props.onDblClick) {
			this.props.onDblClick(this.props.node);
		}
	}

	renderHeader(name, active, type, node, meta, accept) {

		let icon = null;
		if (type === "folder") {
			icon = <FontAwesomeIcon icon={"folder"} />;
		} else if (type === "file") {
			icon = <FontAwesomeIcon icon={getFileTypeIcon(node.ext, node)} />;
		} else {
			icon = <FontAwesomeIcon icon="cloud" />;
		}
		let fnsSelect = {};
		let acceptable = isAcceptedNodeType(type, node, accept);
		if (acceptable) {
			fnsSelect = {
				onClick: this.handleClick
			};
			if (type === "folder") {
				fnsSelect.onTouchStart = this.handleTouchStart;
				fnsSelect.onTouchEnd = this.handleTouchEnd;
			}
		}

		const { selectionEnabled, selectionAvailable, isSelected, skuPhotoMappingMode } = this.props;
		const showSelectionControl = shouldShowDriveSelectionControl({
			selectionEnabled,
			skuPhotoMappingMode,
			node,
			selectionAvailable
		});
		const selectionControl = showSelectionControl ? (
			<label className={`${selectionAvailable ? styles["selection-control"] : `${styles["selection-control"]} ${styles.disabled}`} ${skuPhotoMappingMode ? styles["mapping-selection-control"] : ""}`} onClick={(event) => event.stopPropagation()} title={selectionAvailable ? `${isSelected ? "Remove" : "Add"} ${name || "item"} ${skuPhotoMappingMode ? "from" : "to"} selection` : "This item cannot be selected for bulk actions"}>
				<input
					type="checkbox"
					checked={!!isSelected}
					disabled={!selectionAvailable}
					onChange={this.handleToggleSelected}
					aria-label={`${isSelected ? "Deselect" : "Select"} ${name || "photo"}`}
				/>
				{skuPhotoMappingMode && <span>{isSelected ? "Selected" : "Select photo"}</span>}
			</label>
		) : null;

		let headerClassName = acceptable
			? active
				? "header selected"
				: isSelected
					? "header bulk-selected"
					: "header"
			: "header disabled";
		if (skuPhotoMappingMode) {
			headerClassName = `${headerClassName} ${styles["mapping-row"]} ${isSelected ? styles["mapping-selected"] : ""}`;
		}
		const mappingInteractionProps = skuPhotoMappingMode && acceptable && type === "folder"
			? {
				tabIndex: 0,
				onKeyDown: this.handleMappingKeyDown,
				role: "button",
				"aria-label": `Open folder ${name || ""}`.trim()
			}
			: {};

		return (
			<div className={headerClassName} title={name}>
				<div className="name" style={{ flex: 1, alignItems: "center", minHeight: "50px", padding: "12px 10px", display: "flex", flexDirection: "row", width: "100%", touchAction: type === "folder" ? "manipulation" : undefined }} {...fnsSelect} {...mappingInteractionProps}>
					{selectionControl}
					<div className={styles.icon}>{icon}</div>
					<div className={styles["name-container"]}>{name}</div>
					{skuPhotoMappingMode && type === "folder" && <span className={styles["folder-open-hint"]}>Open</span>}
				</div>
			</div>
		);
	}

	render() {
		const { node, meta, accept } = this.props;
		const { guid, id, name, type } = node;
		const { active } = meta ? meta : node;

		const key = `node-${guid ? guid : id ? id : generateUUID()}`;

		// console.log("Render node", name, toggled, active);
		return <div key={key} className="node" id={`drive-node-${this.context?.driveComponentId}-${node.guid}`}>
			{this.renderHeader(name, active, type, node, meta, accept)}
		</div>;
	}
}

ListNode.contextType = DriveContext;

export default branch((props) => {
	// console.log("Mapping", props.node.guid, props.node);
	if (props.node.guid) {
		return {
			meta: ["drive", "hierarchyMetadata", props.node.guid]
		};
	} else {
		return {};
	}
}, ListNode);
